import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Chart,
} from 'chart.js';
import type { ChartOptions } from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export interface DataPoint {
  dbSPL: number;
  frequency: number;
  ear: number;
}

interface HearingChartProps {
  data: DataPoint[];
}

const hearingZones = [
  {
    start: 0,
    end: 20,
    color: 'rgba(255, 255, 255, 0.6)',
    label: 'Normalny słuch',
  },
  {
    start: 20,
    end: 40,
    color: 'rgba(255, 255, 0, 0.4)',
    label: 'Łagodna utrata słuchu',
  },
  {
    start: 40,
    end: 70,
    color: 'rgba(255, 165, 0, 0.4)',
    label: 'Umiarkowany ubytek słuchu',
  },
  {
    start: 70,
    end: 90,
    color: 'rgba(255, 100, 0, 0.4)',
    label: 'Ciężka utrata słuchu',
  },
  {
    start: 90,
    end: 100,
    color: 'rgba(255, 0, 0, 0.4)',
    label: 'Głęboka utrata słuchu',
  },
];

// Gradientowe tło
const backgroundPlugin = {
  id: 'customBackground',
  beforeDraw(chart: Chart) {
    const {
      ctx,
      chartArea,
      scales: { y },
    } = chart;
    const { left, right, top, bottom } = chartArea;

    // Gradient tła
    const gradient = ctx.createLinearGradient(0, top, 0, bottom);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(1, '#f8f9fa');

    ctx.save();
    ctx.fillStyle = gradient;
    ctx.fillRect(left, top, right - left, bottom - top);
    ctx.restore();

    // Strefy słuchowe
    hearingZones.forEach(zone => {
      const yTop = y.getPixelForValue(zone.start);
      const yBottom = y.getPixelForValue(zone.end);
      ctx.save();
      ctx.fillStyle = zone.color;
      ctx.fillRect(left, yBottom, right - left, yTop - yBottom);
      ctx.restore();

      ctx.save();
      ctx.fillStyle = 'rgba(70, 70, 70, 0.5)';
      ctx.font = 'italic 12px Poppins, Arial';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.fillText(zone.label, left + 5, (yTop + yBottom) / 2);
      ctx.restore();
    });
  },
};

// ChartJS.register(backgroundPlugin);

export const HearingChart: React.FC<HearingChartProps> = ({ data }) => {
  const frequencies = [125, 250, 500, 1000, 2000, 4000, 8000];

  // Dane podzielone na uszy
  const leftEarData = data
    .filter(d => d.ear === -1)
    .sort((a, b) => a.frequency - b.frequency)
    .map(d => ({ x: d.frequency, y: d.dbSPL }));

  const rightEarData = data
    .filter(d => d.ear === 1)
    .sort((a, b) => a.frequency - b.frequency)
    .map(d => ({ x: d.frequency, y: d.dbSPL }));

  const bothEarsData = data
    .filter(d => d.ear === 0)
    .sort((a, b) => a.frequency - b.frequency)
    .map(d => ({ x: d.frequency, y: d.dbSPL }));

  const chartData = {
    datasets: [
      {
        label: 'Lewe ucho',
        data: leftEarData,
        borderColor: '#4F8DF3',
        backgroundColor: '#4F8DF3',
        pointStyle: 'circle',
        pointRadius: 6,
        pointHoverRadius: 8,
        borderWidth: 2,
        tension: 0.4,
      },
      {
        label: 'Prawe ucho',
        data: rightEarData,
        borderColor: '#F35E5E',
        backgroundColor: 'transparent',
        pointStyle: 'circle',
        pointRadius: 7,
        pointHoverRadius: 9,
        pointBorderColor: '#F35E5E',
        pointBackgroundColor: 'transparent',
        pointBorderWidth: 2,
        borderWidth: 2,
        tension: 0.4,
      },
      {
        label: 'Oba',
        data: bothEarsData,
        borderColor: '#4CAF50',
        backgroundColor: 'transparent',
        pointStyle: 'rect',
        pointRadius: 8,
        pointHoverRadius: 10,
        pointBorderColor: '#4CAF50',
        pointBackgroundColor: 'transparent',
        pointBorderWidth: 2,
        borderDash: [5, 5],
        borderWidth: 2,
        tension: 0.4,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          usePointStyle: true,
          color: '#555',
          font: {
            size: 13,
            family: 'Poppins, Arial, sans-serif',
          },
        },
      },
      title: {
        display: true,
        text: 'Wykres Audiometryczny',
        font: {
          size: 18,
          family: 'Poppins, Arial, sans-serif',
        },
        color: '#333',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    scales: {
      x: {
        type: 'category',
        labels: frequencies.map(f => f.toString()),
        grid: {
          color: 'rgba(200, 200, 200, 0.2)',
        },
        title: {
          display: true,
          text: 'Częstotliwość (Hz)',
          color: '#333',
          font: { family: 'Poppins, Arial', size: 14 },
        },
      },
      y: {
        reverse: true,
        min: 0,
        max: 100,
        ticks: { stepSize: 10 },
        grid: {
          color: 'rgba(200, 200, 200, 0.2)',
        },
        title: {
          display: true,
          text: 'Poziom słyszenia (dB)',
          color: '#333',
          font: { family: 'Poppins, Arial', size: 14 },
        },
      },
    },
  };

  return (
    <div className="hearing-chart-container">
      <Line
        key="hearingLine"
        data={chartData}
        options={options}
        plugins={[backgroundPlugin]}
      />
    </div>
  );
};
